import axios from '@/http/axios/index.js';
import { migrationRedirectBypassReason, migrationRoutePath } from '@/utils/platformMigrationHelper';
import { getStoredAccessToken } from '@/utils/localStorageHelpers';
import { migrationDecisionEvent, sendMigrationDecision } from '@/utils/migrationDecisionLog';

var _migrationCache = null;
var _migrationCheckPromise = null;

function getStoredOrganizationId() {
  return localStorage.getItem(process.env.VUE_APP_ACCESS_TOKEN_NAME + 'OrganizationId');
}

function getOrganizationId(org) {
  if (!org) return null;
  return org._id || org.id || org.orgId || null;
}

export function resolveActiveOrganization(orgs, activeOrgId) {
  if (!orgs || orgs.length === 0) return null;
  var orgId = activeOrgId || getStoredOrganizationId();
  if (orgId) {
    for (var i = 0; i < orgs.length; i++) {
      var id = getOrganizationId(orgs[i]);
      if (id != null && String(id) === String(orgId)) return orgs[i];
    }
    return null;
  }
  return orgs[0];
}

export function resolveMigratedPlatformUrl(org, migratedPlatformUrl) {
  return (org && org.migratedPlatformUrl) || migratedPlatformUrl || null;
}

function isMigratedOrg(org) {
  return !!(org && org.isMigrated === true);
}

// The redirect rule. All V1 activity is being steered to V2, so a user goes to
// V2 when ANY of their orgs is migrated, not only the active one (COR-8192).
// The active org only picks whose V2 URL is used: the active org when it is
// migrated, otherwise the first migrated org. So:
// - a migrated active org redirects;
// - a non-migrated active org still redirects when another membership is
//   migrated (mixed membership);
// - a missing stored org id resolves to the first org, and a stale one (a
//   removed membership, the only "inactive" state V1 web can see) resolves to
//   none; either way any migrated membership still redirects.
// The notice and banner differ on purpose: they follow the active org only,
// and show only where no redirect happens (exempt routes, or a migrated org
// with no V2 URL).
// The org's own migratedPlatformUrl wins over a login-level fallback URL.
export function resolveMigratedOrganization(orgs, activeOrgId) {
  if (!orgs || orgs.length === 0) return null;
  var active = resolveActiveOrganization(orgs, activeOrgId);
  if (isMigratedOrg(active)) return active;
  for (var i = 0; i < orgs.length; i++) {
    if (isMigratedOrg(orgs[i])) return orgs[i];
  }
  return null;
}

// One redirect decision, with the reason when it is skipped. Route bypasses are
// checked first, as they always were, because they also capture or clear the
// V2 shop handoff marker. decision is one of:
//   redirected
//   skipped:local_dev        vue-cli-service serve; a production build still redirects
//   skipped:excluded_route   exempt link (S4, inventory) or Store/Orders
//   skipped:fromV2           the tab that just arrived from the V2 shop
//   skipped:no_org           no memberships
//   skipped:not_migrated     no membership is migrated
//   skipped:no_platform_url  migrated, but no V2 URL to send them to
export function decideMigrationRedirect(orgs, migratedPlatformUrl, activeOrgId, route) {
  var list = orgs || [];
  // vue-cli-service serve sets NODE_ENV. A production build still redirects.
  if (process.env.NODE_ENV === 'development') {
    return {
      decision: 'skipped:local_dev',
      url: null,
      activeOrgId: getOrganizationId(resolveActiveOrganization(list, activeOrgId)),
      targetOrgId: null,
      orgCount: list.length,
      migratedOrgCount: list.filter(isMigratedOrg).length,
    };
  }
  var result = {
    decision: null,
    url: null,
    activeOrgId: getOrganizationId(resolveActiveOrganization(list, activeOrgId)),
    targetOrgId: null,
    orgCount: list.length,
    migratedOrgCount: list.filter(isMigratedOrg).length,
  };
  var bypass = migrationRedirectBypassReason(route);
  if (bypass) {
    result.decision = 'skipped:' + bypass;
    return result;
  }
  if (!list.length) {
    result.decision = 'skipped:no_org';
    return result;
  }
  var org = resolveMigratedOrganization(list, activeOrgId);
  if (!org) {
    result.decision = 'skipped:not_migrated';
    return result;
  }
  result.targetOrgId = getOrganizationId(org);
  result.url = resolveMigratedPlatformUrl(org, migratedPlatformUrl);
  result.decision = result.url ? 'redirected' : 'skipped:no_platform_url';
  return result;
}

export function shouldRedirectToMigratedPlatform(orgs, migratedPlatformUrl, activeOrgId, route) {
  return decideMigrationRedirect(orgs, migratedPlatformUrl, activeOrgId, route).decision === 'redirected';
}

// report: { trigger, skipped }. Redirects are always reported; skips only when
// report.skipped is set, so in-app navigation logs a redirect but not every
// route change. Omit report to decide without logging.
function reportDecision(result, route, report) {
  if (!report) return;
  if (result.decision !== 'redirected' && !report.skipped) return;
  sendMigrationDecision(migrationDecisionEvent(result, report.trigger, migrationRoutePath(route)));
}

export function redirectToMigratedPlatform(migratedPlatformUrl) {
  window.location.href = migratedPlatformUrl;
}

export function updateMigrationCache(organizations, migratedPlatformUrl) {
  _migrationCache = { organizations: organizations, migratedPlatformUrl: migratedPlatformUrl };
}

// Cached migration state for the active org, for UI that surfaces it instead of
// redirecting (the migration notice and banner). Only the active org counts: a
// user whose active org has not migrated sees no migration treatment.
// orgPlatformUrl is null when the API gave none — the stuck state the banner
// covers with a region fallback.
export function getActiveMigrationTarget(activeOrgId) {
  var none = { migrated: false, orgId: null, orgPlatformUrl: null };
  if (!_migrationCache) return none;
  var org = resolveActiveOrganization(_migrationCache.organizations, activeOrgId);
  if (!isMigratedOrg(org)) return none;
  return {
    migrated: true,
    orgId: getOrganizationId(org),
    orgPlatformUrl: resolveMigratedPlatformUrl(org, _migrationCache.migratedPlatformUrl),
  };
}

function destinationPath(to) {
  if (!to) return null;
  if (typeof to === 'string') return to.split('?')[0];
  return to.path || null;
}

function destinationHref(to) {
  if (!to || typeof to !== 'object' || !to.fullPath) return destinationPath(to);
  return 'https://local.invalid' + to.fullPath;
}

function migrationRoute(to) {
  return {
    path: destinationPath(to) || '',
    href: destinationHref(to) || '',
  };
}

export function checkCachedMigration(to, report) {
  if (!_migrationCache) return false;
  return checkOrgMigration(
    _migrationCache.organizations,
    _migrationCache.migratedPlatformUrl,
    undefined,
    to ? migrationRoute(to) : null,
    report,
  );
}

export function checkOrgMigration(organizations, migratedPlatformUrl, activeOrgId, route, report) {
  var result = decideMigrationRedirect(organizations, migratedPlatformUrl, activeOrgId, route);
  reportDecision(result, route, report);
  if (result.decision !== 'redirected') return false;
  redirectToMigratedPlatform(result.url);
  return true;
}

export function parseUserOrganizationsResponse(data) {
  var orgs = Array.isArray(data.data) ? data.data : [];
  return { orgs: orgs, migratedPlatformUrl: null };
}

export function ensureMigrationChecked(report) {
  if (_migrationCache) {
    return Promise.resolve(checkCachedMigration(null, report));
  }

  var isLoggedIn = localStorage.getItem(
    process.env.VUE_APP_ACCESS_TOKEN_NAME + 'LoggedIn'
  );
  var accessToken = getStoredAccessToken();
  if (!isLoggedIn || !accessToken) {
    return Promise.resolve(false);
  }

  if (!_migrationCheckPromise) {
    _migrationCheckPromise = axios
      .get('/api/v1/organization/get-user-organizations')
      .then(function(response) {
        var parsed = parseUserOrganizationsResponse(response.data);
        if (parsed.orgs.length) {
          updateMigrationCache(parsed.orgs, parsed.migratedPlatformUrl);
        }
        return checkOrgMigration(parsed.orgs, parsed.migratedPlatformUrl, undefined, null, report);
      })
      .catch(function() {
        return false;
      })
      .finally(function() {
        _migrationCheckPromise = null;
      });
  }

  return _migrationCheckPromise;
}
